// src/student/StudentProjects.js
import React, { useState, useEffect, useCallback } from "react";
import noprojects from "../asset/noprojects.png";
import {
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaCalendarAlt,
  FaLink,
  FaImage,
  FaSpinner,
  FaEye,
} from "react-icons/fa";
import SummaryApi from "../common";
import { useAuth } from "../helpers/AuthContext";
import { FaStopwatch } from "react-icons/fa";
import { toast } from "react-toastify";
const StudentProjects = () => {
  const { studentId } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    project_source_link: "",
    description: "",
    output_image1: null,
    output_image2: null,
    output_image3: null,
    output_image4: null,
    output_image5: null,
  });
  const [previewImages, setPreviewImages] = useState({
    output_image1: null,
    output_image2: null,
    output_image3: null,
    output_image4: null,
    output_image5: null,
  });
  const imageBaseUrl = SummaryApi.Images?.url?.replace("api", "") || "";
  // Fetch assigned projects
  const fetchAssignedProjects = async () => {
    try {
      const res = await fetch(SummaryApi.projects_retrieve_by_student.url, {
        method: SummaryApi.projects_retrieve_by_student.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_id: studentId }),
      });
      const data = await res.json();
      return data.status && Array.isArray(data.data) ? data.data : [];
    } catch (error) {
      console.error("Error fetching assigned projects:", error);
      return [];
    }
  };
  // Fetch student submissions
  const fetchStudentSubmissions = async () => {
    try {
      const res = await fetch(SummaryApi.student_project_retrieve_by_student.url, {
        method: SummaryApi.student_project_retrieve_by_student.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_id: studentId }),
      });
      const data = await res.json();
      return data.status && Array.isArray(data.data) ? data.data : [];
    } catch (error) {
      console.error("Error fetching submissions:", error);
      return [];
    }
  };
  // Combine data
  const fetchProjects = useCallback(async () => {
    if (!studentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [assignedProjects, submissions] = await Promise.all([
        fetchAssignedProjects(),
        fetchStudentSubmissions(),
      ]);
      const submissionMap = submissions.reduce((map, sub) => {
        map[sub.project_id] = sub;
        return map;
      }, {});
const mergedProjects = assignedProjects.map((proj) => {
  const sub = submissionMap[proj.project_id];
  if (sub) {
    return {
      ...proj, 
      project_source_link: sub.project_source_link || "",
      description_submitted: sub.description || "",
      submission_date_submitted: sub.submission_date || null, // Student submit time
      output_image1: sub.output_image1 || proj.output_image1,
      output_image2: sub.output_image2 || proj.output_image2,
      output_image3: sub.output_image3 || proj.output_image3,
      output_image4: sub.output_image4 || proj.output_image4,
      output_image5: sub.output_image5 || proj.output_image5,
      is_review: sub.is_review || 0,
      is_submitted: true,
    };
  }
  return {
    ...proj,
    project_source_link: "",
    description_submitted: "",
    submission_date_submitted: null,
    is_review: 0,
    is_submitted: false,
  };
});
      setProjects(mergedProjects);
    } catch (error) {
      console.error("Error in fetchProjects:", error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [studentId]);
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);
  // Open modal
  const openModal = (project) => {
    setSelectedProject(project);
    setFormData({
      project_source_link: project.project_source_link || "",
      description: project.description_submitted || project.description || "",
      output_image1: null,
      output_image2: null,
      output_image3: null,
      output_image4: null,
      output_image5: null,
    });
    const previews = {};
    for (let i = 1; i <= 5; i++) {
      const field = `output_image${i}`;
      const imgPath = project[field];
      previews[field] = imgPath ? `${imageBaseUrl}${imgPath}` : null;
    }
    setPreviewImages(previews);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
    setFormData({
      project_source_link: "",
      description: "",
      output_image1: null,
      output_image2: null,
      output_image3: null,
      output_image4: null,
      output_image5: null,
    });
    setPreviewImages({
      output_image1: null,
      output_image2: null,
      output_image3: null,
      output_image4: null,
      output_image5: null,
    });
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleImageChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPreviewImages((prev) => ({ ...prev, [field]: previewUrl }));
      setFormData((prev) => ({ ...prev, [field]: file }));
      e.target.value = "";
    }
  };
  const clearImage = (field) => {
    setPreviewImages((prev) => ({ ...prev, [field]: null }));
    setFormData((prev) => ({ ...prev, [field]: null }));
  };
  const getDaysLeft = (dueDate) => {
    if (!dueDate) return "No due date";
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? "s" : ""} left`;
    if (diffDays === 0) return "Due today";
    return "Overdue";
  };
const handleSubmit = async (e) => {
  e.preventDefault();
  if (!selectedProject || submitting || selectedProject.is_submitted) return;
  // === Custom Confirmation Toast ===
  toast.info(
    ({ closeToast }) => (
      <div className="flex flex-col gap-4 p-2 bg-white dark:bg-gray-800 rounded-lg">
        <p className="text-md font-medium text-gray-800 dark:text-gray-200">
          Are you sure you want to submit this project? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => {
              closeToast();
            }}
            className="px-3 py-1 text-md font-medium text-gray-700 bg-gray-200 dark:bg-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            No
          </button>
          <button
            onClick={() => {
              closeToast();
              proceedWithSubmission(); // Call actual submit logic
            }}
            className="px-3 py-1 text-md font-medium text-white bg-[#1f4774] dark:bg-blue-700 rounded hover:bg-[#2a5d8a] dark:hover:bg-blue-800"
          >
            Yes, Submit
          </button>
        </div>
      </div>
    ),
    {
      position: "top-center",
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: false,
      className: "border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg",
    }
  );
};







useEffect(() => {
  if (isModalOpen) {
    document.body.style.overflow = "hidden";   // Stop scrolling
  } else {
    document.body.style.overflow = "auto";     // Enable scrolling again
  }

  return () => {
    document.body.style.overflow = "auto";     // Cleanup
  };
}, [isModalOpen]);












// === Extracted submission logic ===
const proceedWithSubmission = async () => {
  setSubmitting(true);
  const form = new FormData();
  form.append("student_id", studentId);
  form.append("project_id", selectedProject.project_id);
  form.append("course_id", selectedProject.course_id);
  form.append("project_source_link", formData.project_source_link);
  form.append("description", formData.description);
  form.append("instructor_id", 0);
  const submissionDate = new Date().toISOString();
  form.append("submission_date", submissionDate);
  for (let i = 1; i <= 5; i++) {
    const file = formData[`output_image${i}`];
    if (file) form.append(`output_image${i}`, file);
  }
  try {
    const res = await fetch(SummaryApi.student_project_create.url, {
      method: SummaryApi.student_project_create.method,
      body: form,
    });
    const result = await res.json();
    if (result.status) {
      toast.success("Project submitted successfully!", {
        position: "top-center",
        autoClose: 3000,
        className: "dark:bg-gray-800 dark:text-white",
      });
      fetchProjects();
      closeModal();
    } else {
      toast.error(result.message || "Submission failed. Please try again.", {
        position: "top-center",
        autoClose: 4000,
        className: "dark:bg-gray-800 dark:text-white",
      });
    }
  } catch (error) {
    console.error("Submission error:", error);
    toast.error("Network error. Please check your connection.", {
      position: "top-center",
      autoClose: 4000,
      className: "dark:bg-gray-800 dark:text-white",
    });
  } finally {
    setSubmitting(false);
  }
};
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "Invalid";
    }
  };
  const isProjectSubmitted = (project) => project.is_submitted;
  if (loading) {
    return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 py-8 px-4 sm:px-6 lg:px-8">
        <FaSpinner className="animate-spin text-[#1f4774] dark:text-blue-400 text-5xl mb-5" />
        <p className="text-lg text-gray-700 dark:text-gray-300 font-medium">Loading your projects...</p>
      </div>
    );
  }
  if (projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8 bg-gradient-to-br  from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1f4774] dark:text-white mb-10 text-center sm:text-left">
          My Projects
        </h1>
        <img
          src={noprojects}
          alt="No Projects"
          className="w-56 sm:w-72 h-auto object-contain mb-8 drop-shadow-lg"
        />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1f4774] dark:text-white mb-10 text-center sm:text-left">
          My Projects
        </h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {projects.map((project) => {
            const isSubmitted = isProjectSubmitted(project);
            const isReviewed = project.is_review === 1;
            return (
              <div
                key={project.project_id}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <div className="h-56 bg-gray-100 dark:bg-gray-700 border-b-2 border-gray-300 dark:border-gray-600">
                  <img
                    src={
                      project.course_thumbnail
                        ? `${imageBaseUrl}${project.course_thumbnail}`
                        : "https://via.placeholder.com/300x200?text=Course"
                    }
                    alt={project.course_name || project.project_title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = "https://via.placeholder.com/300x200?text=Course";
                    }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#1f4774] dark:text-white mb-2">
                    {project.course_name || "Unknown Course"}
                  </h3>
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
                    {project.project_title}
                  </h4>
                  {/* Status Badge */}
                  <div className="mb-4">
                    {isReviewed ? (
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-4 py-1 rounded-full">
                        <FaCheckCircle className="text-lg" /> Reviewed & Completed
                      </span>
                    ) : isSubmitted ? (
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/30 px-4 py-1 rounded-full">
                        <FaCheckCircle className="text-lg" /> Submitted - Under Review
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 px-4 py-1 rounded-full">
                        <FaClock className="text-lg" /> Pending Submission
                      </span>
                    )}
                  </div>
                  {/* Deadline Info */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                    <FaCalendarAlt className="text-blue-600 dark:text-blue-400" />
 <span>Due: {formatDate(project.submission_date)}</span>
                    <FaStopwatch className="text-orange-600 dark:text-orange-400" />
                    <span>{getDaysLeft(project.submission_date)}</span>
                  </div>
                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3">
                    {/* View Submission Button */}
                    {(isSubmitted || isReviewed) && (
                      <button
                        onClick={() => openModal(project)}
                        className="flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
                      >
                        <FaEye className="text-lg" /> View Submission
                      </button>
                    )}
                    {/* Submit Button - Only if not submitted and not reviewed */}
                    {!isSubmitted && !isReviewed && (
                      <button
                        onClick={() => openModal(project)}
                        className="flex items-center gap-2 text-sm font-semibold text-[#1f4774] dark:text-blue-400 hover:text-[#2a5d8a] dark:hover:text-blue-300 transition-colors"
                      >
                        Submit Project
                        <FaArrowRight className="text-lg" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* MODAL */}
      {isModalOpen && selectedProject && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 dark:bg-opacity-80 flex items-center justify-center p-2 z-50"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
   
<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
            <div className="p-4 space-y-4">
              
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b dark:border-gray-700 pb-2">
                <div className="flex items-center gap-3">
                  <img
                    src={
                      selectedProject.project_image
                        ? `${imageBaseUrl}${selectedProject.project_image}`
                        : "https://via.placeholder.com/60?text=Proj"
                    }
                    alt={selectedProject.project_title}
                    className="w-12 h-12 rounded-lg object-cover border border-gray-300 dark:border-gray-600 shadow-sm"
                    onError={(e) => {
                      e.currentTarget.src = "https://via.placeholder.com/60?text=Proj";
                    }}
                  />
                  <div>
                    <h2 className="text-lg font-bold text-[#1f4774] dark:text-white">
                      {selectedProject.project_title}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{selectedProject.course_name}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                    <FaCalendarAlt className="text-blue-700 dark:text-blue-400" />
                    <span>{formatDate(selectedProject.submission_date)}</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                    <FaStopwatch className="text-orange-600 dark:text-orange-400" />
                    <span>{getDaysLeft(selectedProject.submission_date)}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Status:</span>{" "}
                    {selectedProject.is_review === 1 ? (
                      <span className="text-green-700 dark:text-green-400 font-bold">Reviewed & Completed</span>
                    ) : isProjectSubmitted(selectedProject) ? (
                      <span className="text-indigo-700 dark:text-indigo-400 font-bold">Submitted - Under Review</span>
                    ) : (
                      <span className="text-yellow-700 dark:text-yellow-400 font-bold">Pending Submission</span>
                    )}
                  </div>


                </div>
<button
  onClick={closeModal}
  className="text-gray-500 dark:text-gray-400 dark:hover:text-red-400 text-xl font-bold focus:outline-none 
             w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 ml-4 ml-auto
             dark:border-gray-600 dark:hover:bg-gray-700"
>
  x
</button>

              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-bold text-gray-800 dark:text-white">Project Details</h3>
   
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Due Date:</span>{" "}
                    <span className="flex items-center gap-1 text-gray-800 dark:text-gray-200">
                      <FaCalendarAlt className="text-blue-700 dark:text-blue-400" />
                      {formatDate(selectedProject.submission_date)}
                    </span>
                  </div>
   {isProjectSubmitted(selectedProject) && (
  <div>
    <span className="font-semibold text-gray-700 dark:text-gray-300">Submitted On:</span>{" "}
    <span className="flex items-center gap-1 text-gray-800 dark:text-gray-200">
      <FaCalendarAlt className="text-green-700 dark:text-green-400" />
      {formatDate(selectedProject.submission_date_submitted)}
    </span>
  </div>
)}
       <div className="mt-2 md:col-span-2">
  <span className="font-semibold text-gray-700 dark:text-gray-300">Project Description:</span>
  <p className="mt-1 text-gray-700 dark:text-gray-300 text-sm leading-relaxed max-h-32 overflow-y-auto">
    {selectedProject.description}
  </p>
</div>
                </div>
              </div>
         <form onSubmit={handleSubmit} className="border-t dark:border-gray-700 pt-3 space-y-3">
                <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-2">
                  {isProjectSubmitted(selectedProject)
                    ? "Your Submitted Details"
                    : "Submit Your Project"}
                </h3>
                {/* Source Link */}
                <div className="mb-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    <FaLink className="inline mr-1 text-sm" /> Project Source Link{" "}
                    <span className="text-red-600">*</span>
                  </label>
                  {isProjectSubmitted(selectedProject) ? (
                    <a
                      href={selectedProject.project_source_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 dark:text-blue-400 hover:underline break-all text-sm font-medium"
                    >
                      {selectedProject.project_source_link}
                    </a>
                  ) : (
                    <input
                      type="url"
                      name="project_source_link"
                      value={formData.project_source_link}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-[#1f4774]/20 dark:focus:ring-blue-500/20 focus:border-[#1f4774] dark:focus:border-blue-500 text-sm"
                      placeholder="https://github.com/your/repo"
                    />
                  )}
                </div>
                {/* Description */}
                <div className="mb-2">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Your Project Description
                  </label>
                  {isProjectSubmitted(selectedProject) ? (
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      {selectedProject.description_submitted || "No description provided"}
                    </p>
                  ) : (
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-[#1f4774]/20 dark:focus:ring-blue-500/20 focus:border-[#1f4774] dark:focus:border-blue-500 text-sm"
                      placeholder="Explain your approach..."
                    />
                  )}
                </div>
{/* Output Images */}
<div>
  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
    <FaImage className="inline mr-1 text-sm" /> Output Screenshots
  </label>
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
    {[1, 2, 3, 4, 5].map((i) => {
      const field = `output_image${i}`;
      const imgPath = selectedProject[field];
      const preview = previewImages[field];
      const hasImage = preview || imgPath;
      const isSubmitted = isProjectSubmitted(selectedProject);
      // EDITING ALLOWED ONLY IF PROJECT IS NOT SUBMITTED YET
      const allowEdit = !isSubmitted;
      return (
        <div
          key={i}
          className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg h-32 w-full flex items-center justify-center bg-gray-50 dark:bg-gray-700 overflow-hidden relative group"
        >
          {hasImage ? (
            <>
              <img
                src={preview || `${imageBaseUrl}${imgPath}`}
                alt={`Output ${i}`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.src = "https://via.placeholder.com/150?text=No+Image";
                }}
              />
              {/* CHANGE BUTTON – ONLY IF NOT SUBMITTED */}
              {allowEdit && (
                <div className="absolute inset-0 bg-black bg-opacity-40 dark:bg-opacity-60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <label className="cursor-pointer bg-white dark:bg-gray-800 text-[#1f4774] dark:text-blue-400 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-[#1f4774] dark:hover:bg-blue-600 hover:text-white dark:hover:text-white transition-all">
                    Change
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, field)}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </>
          ) : isSubmitted ? (
            <span className="text-gray-400 dark:text-gray-500 text-xs">No image</span>
          ) : (
            <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center text-gray-600 dark:text-gray-400 hover:text-[#1f4774] dark:hover:text-blue-400 transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e, field)}
                className="hidden"
              />
              <FaImage className="text-lg mb-1" />
              <span className="text-xs font-medium">Upload</span>
            </label>
          )}
        </div>
      );
    })}
  </div>
</div>
                {/* Submit Button - Only for first-time submission */}
                {!isProjectSubmitted(selectedProject) && selectedProject.is_review !== 1 && (
                  <div className="mt-3 text-center">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-5 py-1.5 bg-[#1f4774] dark:bg-blue-600 text-white font-semibold rounded-lg hover:bg-[#2a5d8a] dark:hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 mx-auto text-sm transition-all"
                    >
                      {submitting ? (
                        <>
                          <FaSpinner className="animate-spin text-base" />
                          Submitting...
                        </>
                      ) : (
                        "Submit Project"
                      )}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default StudentProjects;